import React from 'react';
import { Message } from '../../types/chat';
import { MessageBubble } from './MessageBubble';

interface ChatMessagesProps {
  messages: Message[];
  loading?: boolean;
  sending?: boolean;
  onRegenerate?: (index: number) => void;
  isSharedConversation?: string;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({ 
  messages, 
  loading, 
  sending,
  onRegenerate,
  isSharedConversation
}) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const [lastMessageId, setLastMessageId] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Handle auto-scrolling
  const scrollToBottom = React.useCallback((smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  // Scroll on new messages or when messages array changes length
  React.useEffect(() => {
    scrollToBottom(false);
  }, [messages.length, scrollToBottom]);

  // Also scroll when content updates during streaming
  React.useEffect(() => {
    if (sending && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'assistant') {
        scrollToBottom(true);
      }
    }
  }, [messages[messages.length - 1]?.content, sending, scrollToBottom]);

  // Update lastMessageId when a new message is being streamed
  React.useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      // If we're sending and the last message is from the assistant, mark it as live
      if (sending && lastMessage.sender === 'assistant') {
        if (lastMessageId !== lastMessage.id) {
          console.log('Setting lastMessageId to:', lastMessage.id);
          setLastMessageId(lastMessage.id);
        }
      } 
      // If we're not sending anymore, clear the lastMessageId
      else if (!sending && lastMessageId) {
        console.log('Clearing lastMessageId');
        setLastMessageId(null);
      }
    }
  }, [messages, sending, lastMessageId]);

  // Debug logging to help track state changes
  React.useEffect(() => {
    // console.log('ChatMessages state:', { 
    //   currentLastMessageId: lastMessageId, 
    //   sending, 
    //   messagesLength: messages.length,
    //   lastMessageContent: messages.length > 0 ? messages[messages.length - 1].content : null,
    //   actualLastMessageId: messages.length > 0 ? messages[messages.length - 1].id : null,
    //   lastMessageSender: messages.length > 0 ? messages[messages.length - 1].sender : null,
    //   lastMessageWorkflowSteps: messages.length > 0 ? 
    //     messages[messages.length - 1].metadata?.workflow_info?.steps?.length : null
    // });
  }, [messages, sending, lastMessageId]);

  return (
    <div ref={containerRef} className="p-4 md:p-6 space-y-4 overflow-y-auto h-full">
      <div className="space-y-4 w-full max-w-5xl mx-auto">
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            index={index}
            message={message}
            isLiveResponse={message.id === lastMessageId}
            sending={sending && message.id === lastMessageId}
            onRegenerate={onRegenerate}
            isSharedConversation={isSharedConversation}
          />
        ))}
        <div ref={messagesEndRef} data-messages-end />
      </div>
    </div>
  );
};
